/**
 * The local mirror (SPEC §12): every node, the log cursor and the outbox, in IndexedDB.
 * Reads never wait for the network; the outbox survives reloads, so nothing typed is lost.
 * One database per user.
 */
import Dexie, { type Table } from 'dexie'
import type { Node, NodeId, WireTxn } from '@solar/core'

interface Meta {
  key: string
  value: unknown
}

interface OutboxRow {
  i: number
  txn: WireTxn
}

class MirrorDB extends Dexie {
  nodes!: Table<Node, NodeId>
  meta!: Table<Meta, string>
  outbox!: Table<OutboxRow, number>
  constructor(name: string) {
    super(name)
    this.version(1).stores({ nodes: 'id', meta: 'key', outbox: 'i' })
  }
}

export interface MirrorState {
  nodes: Node[]
  cursor: number
  clientId: string | null
  clientSeq: number
  outbox: WireTxn[]
}

export class Mirror {
  private readonly db: MirrorDB
  constructor(userId: string) {
    this.db = new MirrorDB(`solar2-${userId}`)
  }

  async load(): Promise<MirrorState> {
    const [nodes, meta, outbox] = await Promise.all([this.db.nodes.toArray(), this.db.meta.toArray(), this.db.outbox.orderBy('i').toArray()])
    const m = new Map(meta.map((r) => [r.key, r.value]))
    return {
      nodes,
      cursor: (m.get('cursor') as number | undefined) ?? -1,
      clientId: (m.get('clientId') as string | undefined) ?? null,
      clientSeq: (m.get('clientSeq') as number | undefined) ?? 0,
      outbox: outbox.map((r) => r.txn),
    }
  }

  /** Replace everything with a server snapshot (first open, or after a resync). */
  async replaceAll(nodes: readonly Node[], cursor: number): Promise<void> {
    await this.db.transaction('rw', this.db.nodes, this.db.meta, async () => {
      await this.db.nodes.clear()
      await this.db.nodes.bulkPut([...nodes])
      await this.db.meta.put({ key: 'cursor', value: cursor })
    })
  }

  /**
   * One atomic write: confirmed (base) node versions, the cursor they reflect, and the outbox.
   * Keeping them in one IndexedDB transaction means a crash can never leave the mirror and the
   * outbox disagreeing about which transactions are already in the base.
   */
  async persist(p: {
    put: readonly Node[]
    del: readonly NodeId[]
    cursor: number
    outbox: readonly WireTxn[]
    clientSeq: number
  }): Promise<void> {
    await this.db.transaction('rw', this.db.nodes, this.db.meta, this.db.outbox, async () => {
      if (p.put.length) await this.db.nodes.bulkPut([...p.put])
      if (p.del.length) await this.db.nodes.bulkDelete([...p.del])
      await this.db.outbox.clear()
      if (p.outbox.length) await this.db.outbox.bulkPut(p.outbox.map((txn, i) => ({ i, txn })))
      await this.db.meta.bulkPut([
        { key: 'cursor', value: p.cursor },
        { key: 'clientSeq', value: p.clientSeq },
      ])
    })
  }

  async setClientId(id: string): Promise<void> {
    await this.db.meta.put({ key: 'clientId', value: id })
  }

  async destroy(): Promise<void> {
    this.db.close()
    await Dexie.delete(this.db.name)
  }
}
